<template>
  <UIModal v-model="isOpen" fix-header>
    <div class="christmas-modal__badge">
      <img src="public/img/christmas-badge.png" />
      <span
        class="christmas-modal__badge-text"
        :class="{ 'christmas-modal__badge-text--chinese': effectiveMode === 'chinese-new-year' }"
        >{{ badgeText }}</span
      >
    </div>
    <div class="christmas-modal">
      <img :src="heroImageSrc" class="christmas-modal__hero" />
      <div class="christmas-modal__title">
        {{ titleText }}
      </div>
      <div class="christmas-modal__description">
        {{ descriptionText }}
      </div>
      <div class="christmas-modal__btn">
        <button class="btn-primary" @click="handleNavigateToMarketplace">{{ $t('christmasPromoToDiscounts') }}</button>
      </div>
    </div>
  </UIModal>
</template>

<script lang="ts" setup>
import type { PromoModalMode } from '~/composables/usePromoModal';

const props = withDefaults(
  defineProps<{
    promoMode?: PromoModalMode | null;
    /** @deprecated use promoMode instead */
    newYearPromo?: boolean;
  }>(),
  { promoMode: null, newYearPromo: false }
);

const { t } = useI18n();

const effectiveMode = computed(() => {
  if (props.promoMode) return props.promoMode;
  return props.newYearPromo ? 'new-year' : 'christmas';
});

const badgeText = computed(() => {
  const m = effectiveMode.value;
  if (m === 'golden-week') return t('goldenWeekPromoBadge');
  if (m === 'easter') return t('easterPromoBadge');
  if (m === 'st-patrick') return t('stPatrickPromoBadge');
  if (m === 'spring') return t('springPromoBadge');
  if (m === 'chinese-new-year') return t('christmasPromoBadge3');
  if (m === 'new-year') return t('christmasPromoBadge2');
  return t('christmasPromoBadge1');
});
const titleText = computed(() => {
  const m = effectiveMode.value;
  if (m === 'golden-week') return t('goldenWeekPromoTitle');
  if (m === 'easter') return t('easterPromoTitle');
  if (m === 'st-patrick') return t('stPatrickPromoTitle');
  if (m === 'spring') return t('springPromoTitle');
  if (m === 'chinese-new-year') return t('christmasPromoTitle3');
  if (m === 'new-year') return t('christmasPromoTitle2');
  return t('christmasPromoTitle1');
});
const descriptionText = computed(() => {
  const m = effectiveMode.value;
  if (m === 'golden-week') return t('goldenWeekPromoDescription');
  if (m === 'easter') return t('easterPromoDescription');
  if (m === 'st-patrick') return t('stPatrickPromoDescription');
  if (m === 'spring') return t('springPromoDescription');
  if (m === 'chinese-new-year') return t('christmasPromoDescription3');
  if (m === 'new-year') return t('christmasPromoDescription2');
  return t('christmasPromoDescription1');
});
const heroImageSrc = computed(() => {
  const m = effectiveMode.value;
  if (m === 'golden-week') return '/img/golden-week-sales.png';
  if (m === 'easter') return '/img/easter-sales-hero.png';
  if (m === 'st-patrick') return '/img/st-patrick-sales.png';
  if (m === 'spring') return '/img/spring-sales-hero.png';
  if (m === 'chinese-new-year') return '/img/chinese-new-year-banner.png';
  if (m === 'new-year') return '/img/new-year-hero.png';
  return '/img/christmas-hero.png';
});

const isOpen = defineModel<boolean>({ default: false });

const handleNavigateToMarketplace = async () => {
  // Close the modal first
  isOpen.value = false;

  // Navigate to marketplace
  await nextTick();
  setTimeout(() => {
    window.location.href = '/marketplace';
  }, 0);
};
</script>

<style lang="scss" scoped>
.christmas-modal {
  position: relative;
  max-width: 639px;
  width: 100%;
  padding-top: 50px;

  @media (min-width: 640px) {
    padding-top: 0;
  }

  &__badge {
    position: absolute;
    z-index: 1;
    top: -40px;
    width: 400px;
    max-width: calc(100vw - 32px);
    left: 50%;
    transform: translateX(-50%);

    @media (max-width: 639px) {
      top: 6px;
      width: min(400px, calc(100vw - 32px));
    }

    img {
      width: 100%;
      height: auto;
      display: block;
    }
  }

  &__badge-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) translateY(-6px);
    white-space: nowrap;
    z-index: 2;

    font-weight: 700;
    font-size: 32px;
    line-height: 100%;
    color: #fff;
    text-shadow:
      1px 1px 0 #000,
      -1px -1px 0 #000,
      1px -1px 0 #000,
      -1px 1px 0 #000,
      0px 1px 0 #000,
      0px -1px 0 #000,
      1px 0px 0 #000,
      -1px 0px 0 #000;

    filter: drop-shadow(0px 2px 0px #000);

    @media (max-width: 639px) {
      font-size: 24px;
    }

    &--chinese {
      font-size: 22px;

      @media (max-width: 639px) {
        font-size: 18px;
      }
    }
  }

  &__hero {
    width: 100%;
    border-radius: 12px 12px 0 0;

    @media (max-width: 639px) {
      border-radius: 0;
    }
  }

  &__title {
    margin-top: 16px;
    font-weight: 700;
    font-size: 28px;
    line-height: 1;
    text-align: center;

    background: linear-gradient(
      180deg,
      #ffee33 26.91%,
      #ffc800 43.13%,
      #fff15d 66.15%,
      #ffc800 97.02%,
      #fff15d 135.74%
    );
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;

    filter: drop-shadow(1px 1px 0 #ff6100) drop-shadow(-1px -1px 0 #ff6100) drop-shadow(1px -1px 0 #ff6100)
      drop-shadow(-1px 1px 0 #ff6100);

    @media (max-width: 639px) {
      width: 310px;
      margin-left: auto;
      margin-right: auto;
      font-size: 24px;
    }
  }

  &__description {
    font-weight: 400;
    font-size: 16px;
    line-height: 1.4;
    text-align: center;
    color: #fff;
    margin: 16px 40px 24px;

    @media (max-width: 639px) {
      margin: 8px 20px 16px;
    }
  }

  &__btn {
    color: #04202b;
    font-weight: 700;
    font-size: 20px;
    line-height: 100%;
    margin-bottom: 40px;
    display: flex;
    align-items: center;
    justify-content: center;

    button {
      width: fit-content !important;
    }
  }
}
</style>
